import { Tooltip } from '@zendeskgarden/react-tooltips'
import { capitalize } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { CalendarFilterType } from '../../models/CalendarFilter'
import { Item } from '../../models/Item'
import { User } from '../../models/User'
import { currencyFormat, formatDuration } from '../../utils/numbers'
import Label from '../app/design/Label'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { SearchField } from '../common/fields/SearchField'
import { CalendarEmptyState } from './CalendarEmptyState'
import { CalendarSelectCard } from './CalendarSelectCard'

type Props = {
    type: CalendarFilterType
    list: (User | Item)[]
    multiSelect?: boolean
    selected: string[]
    onSelect: (ids: string[]) => void
}

/** @deprecated use SelectList component instead */
export const CalendarSelectCards = ({
    type,
    list,
    multiSelect = true,
    selected,
    onSelect,
}: Props) => {
    const [selectedIds, setSelectedIds] = useState<string[]>(selected)
    const [search, setSearch] = useState('')

    const isSearchMatch = useCallback(
        (stringToSearch: string | undefined) => {
            if (!search) return true

            const escapedSearch = search.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&')
            const regex = new RegExp(`\\b${escapedSearch.replace(/\s+/g, '|')}.*`, 'i')

            return stringToSearch?.match(regex)
        },
        [search]
    )

    const listItems = useMemo(
        () => list.filter((li) => isSearchMatch(li.name)),
        [list, isSearchMatch]
    )

    useEffect(() => {
        setSelectedIds(selected)
    }, [selected])

    const toggleSelect = useCallback(
        (id: string) => {
            const ids = multiSelect
                ? selectedIds.includes(id)
                    ? selectedIds.filter((s) => s !== id)
                    : [...selectedIds, id]
                : [id]
            setSelectedIds(ids)
            onSelect(ids)
        },
        [multiSelect, selectedIds, onSelect]
    )

    const getIsDisabled = useCallback(
        (li: User | Item) =>
            !li.id ||
            (multiSelect &&
                selectedIds.length > 0 &&
                !selectedIds.includes(li.id) &&
                (['class', 'reservation'].includes(li.type ?? '') ||
                    ['class', 'reservation'].includes(
                        listItems.find((l) => l.id === selectedIds[0])?.type ?? ''
                    ))),
        [multiSelect, selectedIds, listItems]
    )

    const getDisabledContext = useCallback(
        (li: User | Item) => {
            const selected = listItems.find((l) => l.id === selectedIds[0])
            return `${selected && ['class', 'reservation'].includes(selected.type ?? '') ? selected.name : li.name} cannot be scheduled with other services`
        },
        [selectedIds, listItems]
    )

    const renderLeftContent = useCallback(
        (li: User | Item) => {
            const pro = li as User
            const item = li as Item

            switch (type) {
                case 'pros':
                    return (
                        <>
                            <ContactAvatar user={pro} size="md" />
                            <Label className="text-gray-700">{pro.name}</Label>
                        </>
                    )
                case 'services':
                case 'classes':
                case 'reservations':
                    return (
                        <div className="flex flex-col gap-1">
                            <Label className="text-gray-700">{item.name}</Label>
                            <Label className="font-normal text-gray-600">
                                {currencyFormat(item.rate)}{' '}
                                {item.duration ? `· ${formatDuration(item.duration)}` : ''}
                            </Label>
                        </div>
                    )
            }
        },
        [type]
    )

    const renderSelectCard = useCallback(
        (li: User | Item) => {
            const isDisabled = getIsDisabled(li)
            const selectCard = (
                <CalendarSelectCard
                    key={li.id}
                    className="items-center gap-4"
                    left={renderLeftContent(li)}
                    multiSelect={multiSelect}
                    isSelected={selectedIds.includes(li.id ?? '')}
                    isDisabled={isDisabled}
                    onSelect={() => toggleSelect(li.id ?? '')}
                />
            )

            return !isDisabled ? (
                selectCard
            ) : (
                <Tooltip key={li.id} content={getDisabledContext(li)}>
                    <div>{selectCard}</div>
                </Tooltip>
            )
        },
        [
            type,
            multiSelect,
            selectedIds,
            renderLeftContent,
            toggleSelect,
            getIsDisabled,
            getDisabledContext,
        ]
    )

    return (
        <>
            {(!!search || listItems.length > 6) && (
                <SearchField
                    value={search}
                    onSearch={setSearch}
                    onClear={() => setSearch('')}
                    autoFocus
                />
            )}
            {!listItems.length && (
                <CalendarEmptyState
                    src={'/icons/checklist.svg'}
                    title={`No ${!search ? capitalize(type) : 'results'}`}
                    subtitle={`${!search ? `You haven't added any ${type} yet` : 'Try again with a different search'}`}
                />
            )}
            {listItems.map((li) => renderSelectCard(li))}
        </>
    )
}
